import React, { useEffect, useRef, useState } from 'react';
import { UrlParam } from 'types/Misc';
import { useParams } from 'react-router-dom';
import { isPreviewMode } from 'utils/preview';
import { useTranslation } from 'react-i18next';
import { isDesktop } from 'react-device-detect';
import { getCustomData } from 'utils/customData';
import { SmsOptInType } from 'types/SmsOptInTypes';
import { isLightColor, shadeChanger } from 'utils/shadeChanger';
import { showToast } from 'components/atomic/Toast/Toast';
import useRedirectLink from 'hooks/useRedirectLink';
import validator from 'validator';
import Cookies from 'js-cookie';
import envVars from 'env-vars';

type UseRebateOptInParams = {
  data: SmsOptInType | null;
  brandName: string;
  brandId: string;
  showRebateStatusPage: boolean;
};

export const useRebateOptIn = ({
  data,
  brandName,
  brandId,
  showRebateStatusPage,
}: UseRebateOptInParams) => {
  const [currentScreen, setCurrentScreen] = useState<'sms' | 'email'>('sms');
  const [emailConsentChecked, toggleEmailConsentChecked] =
    useState<boolean>(false);
  const [smsConsentChecked, toggleSmsConsentChecked] = useState<boolean>(false);
  const [numberField, toggleNumberField] = useState<boolean>(isDesktop);
  const [consentError, setConsentError] = useState<boolean>(false);
  const [otpRequired, setOtpRequired] = useState<boolean>(false);
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [textSent, toggleTextSent] = useState<boolean>(false);
  const [rebateEmail, setRebateEmail] = useState<string>('');
  const [textNumber, setTextNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  const [showRebateStatus, setShowRebateStatus] = useState<boolean>(() =>
    window.location.search.includes('rebateId=')
  );
  const [existingPhone, setExistingPhone] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState<boolean>(false);
  const [useEmailForOtp, setUseEmailForOtp] = useState<boolean>(false);

  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const redirectToExternalLink = useRedirectLink();
  const { id } = useParams<UrlParam>();
  const { t } = useTranslation('translation', { keyPrefix: 'smsOptIn' });

  useEffect(() => {
    if (data?.signupMethod === 'SMS_EMAIL') setCurrentScreen('email');
    else setCurrentScreen('sms');
  }, [data?.signupMethod]);

  useEffect(() => {
    if (
      data?.optInComponent?.emailConsentBox &&
      data?.optInComponent?.emailConsentCheckedByDefault
    )
      toggleEmailConsentChecked(true);
  }, [
    data?.optInComponent?.emailConsentBox,
    data?.optInComponent?.emailConsentCheckedByDefault,
  ]);

  useEffect(() => {
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.checkedAsDefault
    )
      toggleSmsConsentChecked(true);
  }, [data?.optInComponent]);

  useEffect(() => {
    if (!data?.enableTextToOptinOnMobile) toggleNumberField(true);
  }, [data?.enableTextToOptinOnMobile]);

  const defaultLegalSmsConsent = t('defaultSmsDisclaimerText')
    .replace('{{brand_name}}', brandName)
    .replace('https://brij.it/terms', `/p/${brandId}`);

  const defaultLegalEmailConsent = t('defaultEmailLegalText')
    .replace('{{brand_name}}', brandName)
    .replace('https://brij.it/terms', `/p/${brandId}`);

  const isSmsConsentBlocking =
    !!data?.optInComponent?.consentBox &&
    !!data?.optInComponent?.consentRequired &&
    !smsConsentChecked;

  const isEmailConsentBlocking =
    !!data?.optInComponent?.emailConsentBox &&
    !!data?.optInComponent?.emailConsentRequired &&
    !emailConsentChecked;

  const getApiUrl = () =>
    envVars.REACT_APP_STAND_ALONE_MODE === 'false'
      ? envVars.REACT_APP_API_URL
      : '';

  const getRebateId = () => Cookies.get(`rebateId_${id}`) || '';

  const getCustomDataPayload = () => {
    const customDataObject = getCustomData();
    const hasCustomData = Object.keys(customDataObject).length > 0;
    return hasCustomData
      ? Object.entries(customDataObject).map(([key, value]) => ({
          key,
          value,
        }))
      : [];
  };

  const buildSmsConsentPayload = () => {
    const isEmailOnlyChannel = data?.communicationChannel === 'EMAIL';
    return {
      consentType: data?.optInComponent?.consentBox
        ? 'explicit'
        : isEmailOnlyChannel
        ? 'none'
        : 'implicit',
      consentFor: 'sms',
      defaultLanguage: defaultLegalSmsConsent,
      language: data?.customLegalConsent
        ? data?.customLegalText
        : t('defaultSmsDisclaimerText'),
      allowed: data?.optInComponent?.consentBox
        ? smsConsentChecked || null
        : isEmailOnlyChannel
        ? null
        : true,
    };
  };

  const buildEmailConsentPayload = () => ({
    consentType: data?.optInComponent?.emailConsentBox
      ? 'explicit'
      : 'implicit',
    consentFor: 'email',
    defaultLanguage: defaultLegalEmailConsent,
    language: data?.optInComponent?.customizeEmailConsentText
      ? data?.optInComponent?.emailConsentBoxText
      : defaultLegalEmailConsent,
    allowed: data?.optInComponent?.emailConsentBox
      ? emailConsentChecked || null
      : true,
  });

  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length >= 4) {
      const lastFour = cleaned.slice(-4);
      return `(***) ***-${lastFour}`;
    }
    return phoneNumber;
  };

  const isOtpRequired = (): boolean =>
    data?.signupMethod === 'SMS_EMAIL' &&
    rebateEmail !== '' &&
    validator.isEmail(rebateEmail);

  const validateEmail = (value: string) => {
    if (!value) {
      showToast({ message: t('emailInputError'), type: 'error' });
      return false;
    } else {
      if (validator.isEmail(value) && !value.toLowerCase().endsWith('.con')) {
        setConsentError(false);
        return true;
      } else {
        showToast({ message: t('invalidEmailError'), type: 'error' });
        return false;
      }
    }
  };

  const validatePhoneAndConsent = (): boolean => {
    if (!useEmailForOtp && textNumber.replace(/[\s()_-]/g, '').length !== 12) {
      showToast({ message: t('invalidPhoneNumberError'), type: 'error' });
      return false;
    }
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.consentRequired &&
      !smsConsentChecked
    ) {
      setConsentError(true);
      showToast({ message: t('consentRequiredError'), type: 'error' });
      return false;
    } else {
      setConsentError(false);
    }
    return true;
  };

  const showOtpEntry = otpRequired && otpSent;
  const showTextSentSuccess = numberField && textSent;
  const showVerifyPhoneHeader =
    !textSent &&
    !otpRequired &&
    !!data?.emailRebatesEnabled &&
    data?.communicationChannel !== 'SMS';
  const showSmsConsentCheck =
    !showOtpEntry && !!data?.optInComponent?.consentBox && !textSent;
  const showTextUsLink = data?.signupMethod === 'SMS' && !isDesktop;
  const showEmailSkipButton = data?.communicationChannel === 'SMS';
  const showEmailImplicitLegal =
    currentScreen === 'email' && !data?.optInComponent?.emailConsentBox;
  const showOtpLegal =
    currentScreen === 'sms' &&
    data?.communicationChannel === 'EMAIL' &&
    data?.signupMethod === 'SMS_EMAIL' &&
    !otpSent &&
    !data?.optInComponent?.consentBox;
  const showSmsImplicitLegal =
    currentScreen === 'sms' &&
    !data?.optInComponent?.consentBox &&
    !(
      data?.communicationChannel === 'EMAIL' &&
      data?.signupMethod === 'SMS_EMAIL'
    );

  const isLightBg = isLightColor(
    data?.optInComponent?.backgroundColor || '#FFFFFF',
    130
  );
  const bgTextColor = isLightBg ? '!text-black' : '!text-white/90';
  const legalParagraphColor = isLightBg
    ? shadeChanger('#000000', 50)
    : shadeChanger('#FFFFFF', 30);
  const consentMargin = isDesktop ? '!mb-1' : '!mb-2';

  const smsConsentText = data?.customLegalConsent
    ? data?.customLegalText
    : defaultLegalSmsConsent;

  const emailConsentText =
    data?.optInComponent?.emailConsentBoxText &&
    data?.optInComponent?.customizeEmailConsentText
      ? data?.optInComponent?.emailConsentBoxText
      : defaultLegalEmailConsent;

  const otpLegalHtml = t('defaultOtpText').replace(
    '{{privacy_page_url}}',
    isPreviewMode() ? `/c/${brandId}` : `/p/${brandId}`
  );

  const otpPhoneDisplay = existingPhone || formatPhoneNumber(textNumber);
  const existingPhoneFormatted = formatPhoneNumber(existingPhone || '');

  const emailPlaceholder =
    rebateEmail === ''
      ? t('rebateEmailInputPlaceholder')
      : t('rebateEmailInputFilledPlaceholder');
  const textUsButtonLabel = data?.enableTextToOptinOnMobile
    ? data?.buttonCTALinkToMessagingAppText
    : t('sendMessageButton');

  const getPhoneSubmitButtonLabel = (): string => {
    if (data?.optInComponent?.customButtonCTA)
      return data?.optInComponent?.customButtonCTAPhoneNumberSubmissionText;
    if (data?.communicationChannel === null) return t('receiveMessageButton');
    if (
      !otpSent &&
      data?.signupMethod !== 'SMS' &&
      data?.communicationChannel !== 'SMS'
    )
      return t('sendCodeButton');
    return t('receiveMessageButton');
  };
  const phoneSubmitButtonLabel = getPhoneSubmitButtonLabel();

  const existingPhoneButtonLabel = data?.optInComponent?.customButtonCTA
    ? data?.optInComponent?.customButtonCTAPhoneNumberSubmissionText
    : t('sendCodeButton');

  const verifyPhoneDescription = existingPhone
    ? t('existingPhoneMessage') + existingPhoneFormatted
    : t('verifyPhoneNumberDescription');

  const getInputAreaHeight = (): string => {
    if (!numberField) return 'h-[40px]';
    if (textSent) return isDesktop ? 'pt-1 h-0' : 'h-0';
    if (showOtpEntry) return isDesktop ? 'pt-1 h-[180px]' : 'h-[180px]';
    if (existingPhone) return isDesktop ? 'pt-1 h-20' : 'h-20';
    if (data?.signupMethod === 'SMS' && !isDesktop) return 'h-[172px]';
    return isDesktop ? 'pt-1 h-[132px]' : 'h-[132px]';
  };
  const inputAreaHeight = getInputAreaHeight();
  const requestOtp = async (): Promise<boolean> => {
    const API_URL = getApiUrl();
    setOtpLoading(true);
    try {
      const requestBody = useEmailForOtp
        ? { email: rebateEmail }
        : { phoneNumber: textNumber.replace(/[\s()-]/g, '') };

      const response = await fetch(`${API_URL}/shared-api/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        showToast({ message: t('otpSentSuccess'), type: 'success' });
        setOtpSent(true);
        return true;
      } else {
        showToast({ message: t('otpSendError'), type: 'error' });
        return false;
      }
    } catch (error) {
      showToast({ message: t('otpSendError'), type: 'error' });
      console.error('Error requesting OTP:', error);
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (): Promise<boolean> => {
    if (otpCode.length !== 6) {
      showToast({ message: t('otpInvalidLength'), type: 'error' });
      return false;
    }

    const API_URL = getApiUrl();
    setOtpLoading(true);
    try {
      const requestBody = useEmailForOtp
        ? { email: rebateEmail, code: otpCode }
        : {
            phoneNumber: textNumber.replace(/[\s()-]/g, ''),
            code: otpCode,
            email: rebateEmail,
          };

      const response = await fetch(`${API_URL}/shared-api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.success === false) {
          const errorMessage =
            responseData.message || responseData.error || t('otpVerifyError');
          showToast({ message: errorMessage, type: 'error' });
          return false;
        }
        return true;
      } else {
        showToast({ message: t('otpVerifyError'), type: 'error' });
        return false;
      }
    } catch (error) {
      showToast({ message: t('otpVerifyError'), type: 'error' });
      console.error('Error verifying OTP:', error);
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const openSMSApp = async () => {
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.consentRequired &&
      !smsConsentChecked
    ) {
      setConsentError(true);
      showToast({ message: t('consentRequiredError'), type: 'error' });
      return;
    } else setConsentError(false);

    const API_URL = getApiUrl();
    setLoading(true);
    const rebateId = getRebateId();

    if (rebateId) {
      const url = `${API_URL}/app_api/rebate/${rebateId}/redirect`;
      const customData = getCustomDataPayload();

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consent: buildSmsConsentPayload(),
            ...(customData && { customData }),
          }),
        });

        if (!response.ok) {
          console.error('Error:', await response.json());
          showToast({ message: t('errorSendingRequest'), type: 'error' });
        } else {
          const data = await response.json();
          redirectToExternalLink(data.redirectUrl, '_self');
        }
      } catch (error) {
        showToast({ message: t('errorSendingRequest'), type: 'error' });
        console.error('Error:', error);
      }
    } else showToast({ message: t('rebateIdError'), type: 'error' });
    setLoading(false);
  };

  const sendOptInRequest = async () => {
    const API_URL = getApiUrl();
    setLoading(true);
    const rebateId = getRebateId();

    if (rebateId) {
      const url = `${API_URL}/app_api/rebate/${rebateId}/text`;
      const customData = getCustomDataPayload();

      const body = {
        phoneNumber: textNumber.replace(/[\s()-]/g, ''),
        consent: buildSmsConsentPayload(),
        email: rebateEmail || '',
        ...(rebateEmail && {
          emailConsent: buildEmailConsentPayload(),
        }),
        ...(customData && { customData }),
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (response?.status === 409) {
          showToast({
            message: t('rebateAlreadyClaimedError'),
            type: 'error',
          });
        } else if (!response.ok) {
          let errorMessage = t('errorSendingRequest');
          let shouldShowConflictError = false;

          try {
            const errorData = await response.json();
            console.error('Error:', errorData);

            const rawErrorMessage =
              errorData?.error || errorData?.message || '';

            const cleanedMessage = rawErrorMessage
              ? rawErrorMessage.replace(/^Error sending text:\s*/i, '').trim()
              : '';

            errorMessage = cleanedMessage || t('errorSendingRequest');

            if (errorData?.conflict) shouldShowConflictError = true;
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
          }

          if (shouldShowConflictError) {
            showToast({
              message: t('rebateInProgressConflictError'),
              type: 'error',
            });
          } else {
            showToast({ message: errorMessage, type: 'error' });
          }
        } else {
          showToast({
            message: t('messageSentSuccessfully'),
            type: 'success',
          });
          if (showRebateStatusPage) {
            window.history.pushState(
              {},
              '',
              `${window.location.pathname}?rebateId=${rebateId}`
            );
            setShowRebateStatus(true);
          }
          toggleTextSent(true);
        }
      } catch (error) {
        showToast({ message: t('errorSendingRequest'), type: 'error' });
        console.error('Error:', error);
      }
    } else showToast({ message: t('rebateIdError'), type: 'error' });
    setLoading(false);
  };

  const handlePhoneSubmit = async () => {
    if (!validatePhoneAndConsent()) return;

    if (isOtpRequired() && !otpSent) {
      const success = await requestOtp();
      if (success) setOtpRequired(true);
      return;
    }
    await sendOptInRequest();
  };

  const handleOtpSubmit = async () => {
    const verified = await verifyOtp();
    if (verified) {
      await sendOptInRequest();
    }
  };

  const handleResendOtp = async () => {
    setOtpCode('');
    await requestOtp();
  };

  const handleRebateEmailSubmission = async (emailSkipped: boolean = false) => {
    if (emailSkipped) {
      setCurrentScreen('sms');
      return;
    }

    if (
      data?.optInComponent?.emailConsentBox &&
      data?.optInComponent?.emailConsentRequired &&
      !emailConsentChecked
    ) {
      setConsentError(true);
      return;
    }

    if (!validateEmail(rebateEmail)) return;

    const API_URL = getApiUrl();
    setCheckingEmail(true);
    try {
      const response = await fetch(
        `${API_URL}/shared-api/users/check-rebates-email?email=${encodeURIComponent(
          rebateEmail
        )}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.truncatedPhone) {
          setExistingPhone(data.truncatedPhone);
          setUseEmailForOtp(true);
        } else {
          setExistingPhone(null);
          setUseEmailForOtp(false);
        }
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setExistingPhone(null);
      setUseEmailForOtp(false);
    } finally {
      setCheckingEmail(false);
      setCurrentScreen('sms');
    }
  };

  const handleOtpDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    if (!digit) return;

    let newCode: string;
    if (index < otpCode.length) {
      newCode =
        otpCode.substring(0, index) + digit + otpCode.substring(index + 1);
    } else {
      newCode = otpCode + digit;
    }
    setOtpCode(newCode.slice(0, 6));

    if (index < 5) {
      setTimeout(() => otpInputRefs.current[index + 1]?.focus(), 0);
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (otpCode[index]) {
        const newCode =
          otpCode.substring(0, index) + otpCode.substring(index + 1);
        setOtpCode(newCode);
      } else if (index > 0) {
        otpInputRefs.current[index - 1]?.focus();
        const newCode =
          otpCode.substring(0, index - 1) + otpCode.substring(index);
        setOtpCode(newCode);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (pasted) {
      setOtpCode(pasted);
      const focusIdx = Math.min(pasted.length, 5);
      setTimeout(() => otpInputRefs.current[focusIdx]?.focus(), 0);
    }
  };

  const handleOtpFocus = (index: number) => {
    const nextEmpty = otpCode.length;
    if (index > nextEmpty && nextEmpty < 6) {
      otpInputRefs.current[nextEmpty]?.focus();
    }
  };

  // ─── Return ────────────────────────────────────────────────────────

  return {
    t,

    currentScreen,
    rebateEmail,
    setRebateEmail,
    textNumber,
    setTextNumber,
    otpCode,
    existingPhone,
    numberField,
    toggleNumberField,
    textSent,
    smsConsentChecked,
    toggleSmsConsentChecked,
    emailConsentChecked,
    toggleEmailConsentChecked,
    consentError,
    showRebateStatus,
    loading,
    otpLoading,
    checkingEmail,
    otpInputRefs,

    showOtpEntry,
    showTextSentSuccess,
    showVerifyPhoneHeader,
    showSmsConsentCheck,
    showTextUsLink,
    showEmailSkipButton,
    showEmailImplicitLegal,
    showOtpLegal,
    showSmsImplicitLegal,
    isSmsConsentBlocking,
    isEmailConsentBlocking,

    textUsButtonLabel,
    phoneSubmitButtonLabel,
    existingPhoneButtonLabel,
    verifyPhoneDescription,
    inputAreaHeight,
    bgTextColor,
    legalParagraphColor,
    consentMargin,
    smsConsentText,
    emailConsentText,
    otpLegalHtml,
    otpPhoneDisplay,
    existingPhoneFormatted,
    emailPlaceholder,

    openSMSApp,
    handlePhoneSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleOtpDigitChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleOtpFocus,
    handleRebateEmailSubmission,
  };
};
